import { useState } from "react";
import { Upload, X, ArrowUp, ArrowDown, Star, FileText, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import crm, { apiErr } from "../crmApi";
import { fileSrc } from "./WebsiteApp";

const ACCEPT = { image: "image/jpeg,image/png,image/webp,image/gif", video: "video/mp4,video/webm,video/quicktime", document: "application/pdf,image/*" };

function useUploader(kind, propertyId) {
  const [progress, setProgress] = useState(null);
  const run = async (files) => {
    const urls = [];
    for (const f of files) {
      setProgress({ name: f.name, pct: 0 });
      try { const m = await crm.wm.upload(f, kind, propertyId, (pct) => setProgress({ name: f.name, pct })); urls.push(m.url); }
      catch (e) { toast.error(`Upload failed for ${f.name}: ${apiErr(e)}. Your other edits are kept.`); }
    }
    setProgress(null);
    return urls;
  };
  return [progress, run];
}

const Progress = ({ p }) => p && (
  <div className="mt-2 text-xs text-pbs-grey" data-testid="upload-progress">
    <div className="flex justify-between"><span className="truncate"><Loader2 size={12} className="inline animate-spin mr-1" />{p.name}</span><span>{p.pct}%</span></div>
    <div className="h-1.5 bg-pbs-bg rounded-full mt-1 overflow-hidden"><div className="h-full pbs-gradient transition-[width]" style={{ width: `${p.pct}%` }} /></div>
  </div>
);

const Thumb = ({ url, kind }) => kind === "image" ? <img src={fileSrc(url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
  : kind === "video" ? <video src={fileSrc(url)} className="w-full h-full object-cover" muted /> : <span className="w-full h-full flex items-center justify-center text-pbs-purple"><FileText size={22} /></span>;

export function MediaList({ label, kind, items, onChange, propertyId, cover, onCover, testid }) {
  const [progress, run] = useUploader(kind, propertyId);
  const add = async (e) => { const files = [...e.target.files]; e.target.value = ""; const urls = await run(files); if (urls.length) onChange([...items, ...urls]); };
  const move = (i, d) => { const a = [...items]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-pbs-dark/70">{label} <span className="text-pbs-grey">({items.length})</span></p>
        <label className="text-xs font-bold text-pbs-purple cursor-pointer inline-flex items-center gap-1"><Upload size={13} /> Upload
          <input type="file" multiple accept={ACCEPT[kind]} className="hidden" onChange={add} disabled={!!progress} data-testid={`${testid}-input`} /></label>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {items.map((u, i) => (
          <div key={u + i} className={`relative aspect-[4/3] rounded-lg overflow-hidden bg-pbs-bg border-2 ${cover === u ? "border-pbs-gold" : "border-transparent"} group`}>
            <Thumb url={u} kind={kind} />
            {kind === "video" && <Film size={14} className="absolute top-1 left-1 text-white drop-shadow" />}
            {cover === u && <span className="absolute top-1 left-1 bg-pbs-gold text-pbs-plum text-[9px] font-extrabold px-1.5 rounded">COVER</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
              {i > 0 && <button type="button" onClick={() => move(i, -1)} className="text-white p-0.5" title="Move earlier"><ArrowUp size={13} /></button>}
              {i < items.length - 1 && <button type="button" onClick={() => move(i, 1)} className="text-white p-0.5" title="Move later"><ArrowDown size={13} /></button>}
              {onCover && cover !== u && <button type="button" onClick={() => onCover(u)} className="text-pbs-gold p-0.5" title="Set as cover" data-testid={`set-cover-${i}`}><Star size={13} /></button>}
              <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-pbs-coral p-0.5" title="Remove" data-testid={`${testid}-remove-${i}`}><X size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full text-xs text-pbs-grey py-3">Nothing uploaded yet.</p>}
      </div>
      <Progress p={progress} />
    </div>
  );
}

export function MediaSingle({ label, kind, value, onChange, propertyId, testid }) {
  const [progress, run] = useUploader(kind, propertyId);
  const add = async (e) => { const files = [...e.target.files].slice(0, 1); e.target.value = ""; const [url] = await run(files); if (url) onChange(url); };
  return (
    <div data-testid={testid}>
      <p className="text-xs font-semibold text-pbs-dark/70 mb-2">{label}</p>
      <div className="flex items-center gap-3">
        <div className="w-32 aspect-[4/3] rounded-lg overflow-hidden bg-pbs-bg border border-pbs-line shrink-0">{value ? <Thumb url={value} kind={kind} /> : <span className="w-full h-full flex items-center justify-center text-[11px] text-pbs-grey">None</span>}</div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-pbs-purple cursor-pointer inline-flex items-center gap-1"><Upload size={13} /> {value ? "Replace" : "Upload"}
            <input type="file" accept={ACCEPT[kind]} className="hidden" onChange={add} disabled={!!progress} data-testid={`${testid}-input`} /></label>
          {value && <a href={fileSrc(value)} target="_blank" rel="noreferrer" className="text-xs text-pbs-grey hover:text-pbs-purple">Open</a>}
          {value && <button type="button" onClick={() => onChange("")} className="text-xs font-bold text-pbs-coral text-left">Remove</button>}
        </div>
      </div>
      <Progress p={progress} />
    </div>
  );
}
